/**
 * Member import staging rules — logic THUẦN (không framework, không DB) cho nhập danh sách thành viên
 * → chuẩn hoá + validate + dedup (US-E2.F3-0081 · @UC0027, Phase 3b MVP-locked).
 *
 * Trái tim quyết định (DRY — tái dùng api staging create/patch + confirm; test ~100%):
 *  - normalizeEmailValue: trim + lowercase (mirror auth email-normalizer 0007/0008 — canonical dedup).
 *  - normalizePhoneE164: chuẩn hoá SĐT về E.164 (+<country><number>) cho MATCH/dedup. Mặc định VN (+84):
 *    "0912..." → "+84912...". Chỉ dùng để match/dedup danh tính — C1 no-SMS: KHÔNG gửi SMS.
 *  - validateImportRow: cờ lỗi email/phone sai format (KHÔNG chặn nhập — chỉ hiển thị, AC3).
 *  - dedupRowKey: khoá dedup canonical (email ưu tiên; phone dự phòng) để loại row trùng trong CÙNG batch.
 *
 * Pure ⇒ tái dùng giữa api và mobile/worker. KHÔNG log PII (email/phone) — caller redact ở app layer.
 */

import { hasDisallowedPersonNameChars, normalizePersonName } from "./person-name.js";

/** Thông điệp cờ lỗi validate (một nguồn sự thật api ↔ mobile — khớp AC3). */
export const IMPORT_ERR_NO_IDENTITY = "Cần email hoặc số điện thoại.";
export const IMPORT_ERR_BAD_EMAIL = "Email không hợp lệ.";
export const IMPORT_ERR_BAD_PHONE = "Số điện thoại không hợp lệ.";
/**
 * 🔴 NAME-SANITIZE-IMPORT-RECIPROCAL (QĐ PO 03/10, @UC0027 @UC0055): tên do O/A VIẾT (file CSV, dán tay) có ký tự ngoài allowlist tên người
 * (`hasDisallowedPersonNameChars`, `person-name.ts` — MỘT luật với biệt danh/tên suất/câu trợ lý) ⇒ cờ lỗi CỦA DÒNG, không chặn cả lô.
 */
export const IMPORT_ERR_BAD_NAME = "Tên chỉ gồm chữ, số, khoảng trắng và các dấu ' - ( ).";

/**
 * Mã ổn định `{ field, reason }` của từng cờ lỗi dòng (§7: client map mã ⇒ câu; câu tiếng Việt ở `errors` là bản hiển thị cũ, giữ cho client
 * đang đọc nó). `contact` = cặp email|phone (cờ "cần email hoặc số điện thoại" không thuộc riêng ô nào). `reason` là tập con của
 * `VALIDATION_REASONS` (contracts) — cùng từ vựng với 400 `VALIDATION_FAILED`.
 */
export const IMPORT_ROW_ISSUE_FIELDS = ["name", "email", "phone", "contact"] as const;
export const IMPORT_ROW_ISSUE_REASONS = ["format", "required"] as const;
export interface ImportRowIssue {
  readonly field: (typeof IMPORT_ROW_ISSUE_FIELDS)[number];
  readonly reason: (typeof IMPORT_ROW_ISSUE_REASONS)[number];
}

/**
 * Bảng ĐÓNG cờ ⇒ mã. `errors` của một dòng (kể cả dòng đã lưu từ trước) chỉ chứa các hằng này (hoặc câu cũ của chúng) ⇒ suy ra mã
 * lúc đọc, không cần cột mới.
 * 🔴 IMPORT-ISSUE-KEY: CÂU là khoá ⇒ đổi câu một hằng `IMPORT_ERR_*` thì GIỮ câu cũ ở đây (cùng mã), kẻo dòng nháp đã lưu mất `issues`
 * lặng lẽ — THÊM câu mới, KHÔNG xoá câu cũ. Khoá bằng literal ở `member-import.test.ts` §IMPORT-ISSUE-KEY
 * (`CURRENT_SENTENCES` + `LEGACY_SENTENCES` chỉ-thêm).
 */
const IMPORT_ROW_ISSUE_BY_ERROR: ReadonlyMap<string, ImportRowIssue> = new Map([
  [IMPORT_ERR_BAD_NAME, { field: "name", reason: "format" }],
  [IMPORT_ERR_BAD_EMAIL, { field: "email", reason: "format" }],
  [IMPORT_ERR_BAD_PHONE, { field: "phone", reason: "format" }],
  [IMPORT_ERR_NO_IDENTITY, { field: "contact", reason: "required" }],
]);

/** Cờ lỗi dòng ⇒ mã `{ field, reason }` (giữ thứ tự). Chuỗi ngoài bảng bị bỏ — không bịa mã cho thứ không biết. */
export function importRowIssues(errors: readonly string[]): ImportRowIssue[] {
  return errors.flatMap((e) => {
    const issue = IMPORT_ROW_ISSUE_BY_ERROR.get(e);
    return issue ? [issue] : [];
  });
}

/** Mã quốc gia mặc định khi số nội địa bắt đầu bằng 0 (VN theo locale sản phẩm). */
export const DEFAULT_PHONE_COUNTRY_CODE = "84";

/**
 * Email hợp lệ tối thiểu tại tầng domain (local@domain.tld). KHÔNG thay validate biên (Zod .email)
 * — đây là cờ lỗi hiển thị staging (AC3), chấp nhận "đủ tốt" cho dedup/preview.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** E.164 canonical: dấu + rồi 8..15 chữ số, chữ số đầu 1-9 (ITU-T E.164). */
const E164_PATTERN = /^\+[1-9]\d{7,14}$/;

/**
 * Chuẩn hoá email: trim + lowercase (canonical dedup + lưu). Mirror auth normalizeEmail (0007/0008).
 * KHÔNG validate ở đây (validate riêng ở validateImportRow — tách chuẩn-hoá khỏi hợp-lệ).
 */
export function normalizeEmailValue(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Chuẩn hoá SĐT về E.164 (+<country><national>) để MATCH/dedup + LƯU (C1 no-SMS: KHÔNG gửi). Mọi
 * cách nhập cùng 1 số VN đều quy về 1 canonical (nhập linh hoạt, lưu thống nhất). Với VN (+84), ví dụ
 * `934988681` / `0934988681` / `84934988681` / `+84934988681` / `+840934988681` → `+84934988681`.
 * Quy tắc (theo thứ tự, `cc` = countryCode mặc định VN "84"):
 *  - Bỏ ký tự trình bày (space, -, (, ), ., …), giữ `+` và chữ số.
 *  - "+<digits>" → phần số quốc tế; bỏ trunk `0` dư ngay sau `cc` ("+<cc>0<nat>" → "+<cc><nat>").
 *  - "00<digits>" (IDD) → coi như "+<digits>" rồi áp cùng quy tắc trunk-0.
 *  - "0<national>" → "+<cc><national>" (bỏ đúng 1 số 0 đầu — số nội địa).
 *  - "<cc><national>" (bắt đầu bằng cc, KHÔNG `+`/`0`) → "+<cc><national>" (nhập thiếu dấu +).
 *  - Số thuần còn lại (bare national) → "+<cc><national>" (mặc định quốc gia sản phẩm).
 * Trả chuỗi E.164 hợp lệ, hoặc **null** nếu không chuẩn hoá được. Caller quyết cờ lỗi.
 */
export function normalizePhoneE164(
  phone: string,
  countryCode: string = DEFAULT_PHONE_COUNTRY_CODE,
): string | null {
  // Giữ lại + và chữ số; loại mọi ký tự trình bày (space, -, (, ), ., …).
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (cleaned.length === 0) return null;

  let candidate: string;
  if (cleaned.startsWith("+")) {
    // Quốc tế: bỏ trunk-0 dư ngay sau country code (vd +840934... → +84934...).
    candidate = "+" + stripTrunkZeroAfterCc(cleaned.slice(1), countryCode);
  } else if (cleaned.startsWith("00")) {
    // IDD prefix quốc tế "00" → "+"; cùng xử lý trunk-0.
    candidate = "+" + stripTrunkZeroAfterCc(cleaned.slice(2), countryCode);
  } else if (cleaned.startsWith("0")) {
    // Số nội địa "0<national>" → "+<cc><national>" (bỏ đúng 1 số 0 đầu).
    candidate = "+" + countryCode + cleaned.slice(1);
  } else if (cleaned.startsWith(countryCode)) {
    // "<cc><national>" nhập thiếu dấu + → "+<cc><national>"; bỏ trunk-0 dư nếu có.
    candidate = "+" + stripTrunkZeroAfterCc(cleaned, countryCode);
  } else {
    // Bare national (không +/0/cc) → gắn country code mặc định của sản phẩm.
    candidate = "+" + countryCode + cleaned;
  }

  return E164_PATTERN.test(candidate) ? candidate : null;
}

/** Bỏ đúng 1 số 0 "trunk" dư ngay sau country code: "<cc>0<nat>" → "<cc><nat>". Ngược lại giữ nguyên. */
function stripTrunkZeroAfterCc(digits: string, countryCode: string): string {
  if (digits.startsWith(`${countryCode}0`)) {
    return countryCode + digits.slice(countryCode.length + 1);
  }
  return digits;
}

/**
 * Hiển thị SĐT canonical (E.164) về dạng nội địa quen thuộc: "+<cc><national>" → "0<national>" (VN).
 * Chỉ cho HIỂN THỊ — lưu/match vẫn dùng E.164. Không match country code → trả nguyên (đã là dạng khác).
 */
export function formatPhoneNational(
  e164: string,
  countryCode: string = DEFAULT_PHONE_COUNTRY_CODE,
): string {
  const prefix = `+${countryCode}`;
  if (e164.startsWith(prefix)) return "0" + e164.slice(prefix.length);
  return e164;
}

/** Một row đã chuẩn hoá + kết quả validate (cờ lỗi để hiển thị staging — AC3). */
export interface ValidatedImportRow {
  readonly name: string;
  /** Email canonical (lowercased) nếu hợp lệ, ngược lại null. */
  readonly normalizedEmail: string | null;
  /** SĐT E.164 nếu chuẩn hoá được + hợp lệ, ngược lại null. */
  readonly normalizedPhone: string | null;
  /** true nếu tên hợp lệ VÀ có ÍT NHẤT 1 định danh hợp lệ (email hoặc phone). */
  readonly valid: boolean;
  /** Danh sách cờ lỗi (rỗng nếu valid). Hiển thị cho admin sửa — KHÔNG chặn nhập. */
  readonly errors: string[];
}

/**
 * Validate + chuẩn hoá 1 row nhập (AC3). KHÔNG ném — luôn trả kết quả kèm cờ lỗi (staging không chặn nhập).
 *  - email cung cấp nhưng sai format → cờ IMPORT_ERR_BAD_EMAIL, normalizedEmail=null.
 *  - phone cung cấp nhưng không chuẩn hoá được → cờ IMPORT_ERR_BAD_PHONE, normalizedPhone=null.
 *  - KHÔNG có định danh hợp lệ nào → cờ IMPORT_ERR_NO_IDENTITY, valid=false.
 *  - 🔴 tên có ký tự ngoài allowlist tên người → cờ IMPORT_ERR_BAD_NAME (đứng ĐẦU), valid=false — dòng không được mời tới khi admin sửa
 *    (NAME-SANITIZE-IMPORT-RECIPROCAL, QĐ PO 03/10). KHÔNG tự sửa: `name` giữ chữ người viết.
 * `name` = `normalizePersonName` (NFKC + nháy cong ⇒ `'`) rồi trim — chuỗi KIỂM = chuỗi LƯU (review #1158 M8). Biên Zod đã chuẩn hoá + trim
 * cùng hàm; chuẩn hoá lại ở đây là phòng thủ (idempotent) cho đường PATCH ghép tên cũ đã lưu.
 */
export function validateImportRow(row: {
  name: string;
  email?: string | null;
  phone?: string | null;
}): ValidatedImportRow {
  const name = normalizePersonName(row.name).trim();
  const errors: string[] = [];
  const nameOk = !hasDisallowedPersonNameChars(name);
  if (!nameOk) errors.push(IMPORT_ERR_BAD_NAME);

  const rawEmail = row.email?.trim() ?? "";
  const rawPhone = row.phone?.trim() ?? "";

  let normalizedEmail: string | null = null;
  if (rawEmail.length > 0) {
    const canonical = normalizeEmailValue(rawEmail);
    if (EMAIL_PATTERN.test(canonical)) {
      normalizedEmail = canonical;
    } else {
      errors.push(IMPORT_ERR_BAD_EMAIL);
    }
  }

  let normalizedPhone: string | null = null;
  if (rawPhone.length > 0) {
    const e164 = normalizePhoneE164(rawPhone);
    if (e164) {
      normalizedPhone = e164;
    } else {
      errors.push(IMPORT_ERR_BAD_PHONE);
    }
  }

  // Không có định danh nào (cả email lẫn phone trống) → không mời được.
  if (rawEmail.length === 0 && rawPhone.length === 0) {
    errors.push(IMPORT_ERR_NO_IDENTITY);
  }

  // valid = tên hợp lệ VÀ có ít nhất 1 định danh HỢP LỆ (email hoặc phone chuẩn hoá được).
  const valid = nameOk && (normalizedEmail !== null || normalizedPhone !== null);

  return { name, normalizedEmail, normalizedPhone, valid, errors };
}

/**
 * Khoá dedup canonical cho 1 row đã validate (loại row trùng trong CÙNG batch trước khi lưu/confirm).
 * Ưu tiên email (kênh mời), dự phòng phone. null nếu không có định danh hợp lệ (không dedup được →
 * caller giữ nguyên, sẽ skip khi confirm). 🔴 Dòng `valid=false` (vd tên sai ký tự mà email hợp lệ) cũng null — dòng lỗi không được
 * nuốt dòng hợp lệ cùng email phía sau (trước NAME-SANITIZE-IMPORT-RECIPROCAL, có khoá ⇔ valid; giữ nguyên tương đương đó).
 */
export function dedupRowKey(row: ValidatedImportRow): string | null {
  if (!row.valid) return null;
  if (row.normalizedEmail) return `email:${row.normalizedEmail}`;
  if (row.normalizedPhone) return `phone:${row.normalizedPhone}`;
  return null;
}

/**
 * Loại row TRÙNG trong CÙNG batch (staging sạch — preview không thừa dòng). Giữ dòng ĐẦU cho mỗi
 * dedupRowKey (email/phone canonical); dòng sau trùng key → BỎ. Row không có key hợp lệ (invalid —
 * dedupRowKey null) → LUÔN giữ (không gộp nhầm các row lỗi khác nhau, còn hiển thị để admin sửa).
 * Giữ thứ tự xuất hiện gốc. Pure — tái dùng ở createBatch (import CSV cùng email 2 lần → 1 staging row).
 */
export function dedupValidatedRows(
  rows: readonly ValidatedImportRow[],
): ValidatedImportRow[] {
  const seen = new Set<string>();
  const result: ValidatedImportRow[] = [];
  for (const row of rows) {
    const key = dedupRowKey(row);
    if (key === null) {
      // Không định danh hợp lệ → giữ nguyên (không dedup được, cần hiển thị lỗi).
      result.push(row);
      continue;
    }
    if (seen.has(key)) continue; // trùng dòng trước → bỏ.
    seen.add(key);
    result.push(row);
  }
  return result;
}
